import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Squares2X2Icon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
  StarIcon,
  BoltIcon,
  FolderIcon,
  CalendarIcon,
  ShieldExclamationIcon,
  ArrowTrendingUpIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
  BeakerIcon,
  LightBulbIcon,
  ChevronLeftIcon,
  ChevronRightIcon as ChevronRightIconExpand,
  SparklesIcon,
  CpuChipIcon,
  EyeIcon,
  CircleStackIcon,
  ShieldCheckIcon as ShieldIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import BrainIcon from './common/BrainIcon';
import { useModules } from '../hooks/useModules'; // New hook
import { useAuth } from '../contexts/AuthContext.jsx';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { BRAND } from '../constants/brand';

function Sidebar() {
  // Load collapsed state from localStorage, default to false (expanded)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Replace hardcoded sidebarGroups with dynamic loading
  const { sidebarGroups, loading: modulesLoading } = useModules();

  // Smart group expansion - no default open groups
  const [openGroups, setOpenGroups] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Effect to handle smart group expansion based on current route
  useEffect(() => {
    const newOpenGroups = {};
    
    // Check each group to see if current route matches any of its items
    // Only auto-expand for exact matches, not for Jobs section
    sidebarGroups.forEach((group) => {
      const hasActiveItem = group.items.some(item => location.pathname === item.to);
      if (hasActiveItem && group.label !== "Jobs") {
        newOpenGroups[group.label] = true;
      }
    });
    
    setOpenGroups(prev => ({ ...prev, ...newOpenGroups }));
  }, [location.pathname, sidebarGroups]); // Added sidebarGroups to dependency array

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const toggleGroup = (label) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Auto-expand sidebar when clicking navigation links while collapsed
  const handleNavClick = (e) => {
    if (isCollapsed) {
      e.preventDefault();
      setIsCollapsed(false);
      // Use setTimeout to navigate after expansion animation
      setTimeout(() => {
        const href = e.currentTarget.getAttribute('href');
        if (href) {
          window.location.href = href;
        }
      }, 300);
    }
  };

  // Handle click anywhere on sidebar to expand when collapsed
  const handleSidebarClick = (e) => {
    if (isCollapsed) {
      // Don't expand if clicking on the toggle button
      if (e.target.closest('button[title*="Expand sidebar"]')) {
        return;
      }
      setIsCollapsed(false);
    }
  };

  if (modulesLoading) {
    return <div className="sidebar-loading">Loading modules...</div>;
  }

  return (
    <div 
      className={`sidebar h-full flex flex-col border-r transition-all duration-300 ${isCollapsed ? 'w-16' : 'min-w-[260px]'} ${isCollapsed ? 'cursor-pointer' : ''}`}
      style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
        backdropFilter: 'blur(20px)',
        borderRight: '1px solid rgba(156, 163, 175, 0.2)',
        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.06)'
      }}
      onClick={handleSidebarClick}
    >
      {/* Header */}
      <div className={`flex items-center ${isCollapsed ? 'justify-center px-0' : 'gap-3 px-4'} py-4 border-b border-gray-200/40 relative`}>
        <div className={`bg-gray-900 rounded-full p-3 flex items-center justify-center shadow-xl ${isCollapsed ? 'mx-auto' : ''} text-white`}>
          <BrainIcon className="w-5 h-5" />
        </div>
        <div className={`flex flex-col transition-all duration-300 ${isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
          <span className="font-bold text-lg leading-tight whitespace-nowrap text-gray-900">
            {BRAND.product}
          </span>
          <span className="text-[10px] leading-tight text-gray-500 whitespace-nowrap">
            by {BRAND.parentCompany}
          </span>
        </div>
        <button 
          className={`text-gray-400 hover:text-gray-600 transition-all duration-300 ${isCollapsed ? 'absolute top-4 right-2' : 'ml-auto'}`}
          onClick={(e) => {
            e.stopPropagation();
            toggleCollapsed();
          }}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRightIconExpand className="h-4 w-4" />
          ) : (
            <ChevronLeftIcon className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* My View */}
      <div className={`${isCollapsed ? '' : 'px-4'} ${isCollapsed ? 'mt-2' : 'mt-0'}`}>
        {isCollapsed ? (
          <div className="flex justify-center">
            <Link 
              to="/" 
              className={`flex items-center justify-center w-10 h-10 rounded-xl font-medium text-base ${isActive('/') ? "bg-gray-100 text-gray-900" : "text-gray-700 hover:bg-gray-50"} transition-all duration-300`}
              title="My View"
              onClick={handleNavClick}
            >
              <Squares2X2Icon className="h-5 w-5" />
            </Link>
          </div>
        ) : (
          <Link 
            to="/" 
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-base ${isActive('/') ? "bg-gray-100 text-gray-900" : "text-gray-700 hover:bg-gray-50"} transition-all duration-300`}
            onClick={handleNavClick}
          >
            <Squares2X2Icon className="h-5 w-5" />
            <span>My View</span>
          </Link>
        )}
      </div>
      
      {/* Collapsible Groups */}
      <div className={`flex-1 overflow-y-auto ${isCollapsed ? 'mt-2' : 'px-4 mt-2'}`}>
        {sidebarGroups.map((group) => (
          <div key={group.label} className={isCollapsed ? 'mb-2' : 'mb-1'}>
            {isCollapsed ? (
              <div className="flex justify-center">
                <button
                  className={`flex items-center justify-center w-10 h-10 rounded-xl text-gray-700 hover:bg-gray-50 font-semibold text-sm focus:outline-none transition-all duration-300`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCollapsed(false);
                  }}
                  title={group.label}
                >
                  <group.icon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <>
                <button
                  className={`flex items-center w-full px-4 py-2 rounded-xl text-gray-700 hover:bg-gray-50 font-semibold text-sm focus:outline-none transition-all duration-300`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleGroup(group.label);
                  }}
                >
                  <group.icon className="h-5 w-5" />
                  <span className="ml-2">{group.label}</span>
                  <span className="ml-auto">
                    {openGroups[group.label] ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </span>
                </button>
                <div className={`ml-8 mt-1 transition-all duration-300 ${!openGroups[group.label] ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-96'}`}>
                  {group.items.map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      className={`flex items-center gap-2 px-2 py-1 rounded-lg text-sm ${isActive(item.to) ? "bg-gray-100 text-gray-900 font-semibold" : "text-gray-600 hover:bg-gray-50"} transition-all duration-300`}
                      onClick={handleNavClick}
                    >
                      <item.icon className="h-4 w-4" />
                      <span className="whitespace-nowrap">{item.label}</span>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      
      {/* Risk Hubs - Special Navigation */}
      <div className={`${isCollapsed ? '' : 'px-4'} mt-4 flex flex-col gap-2 transition-all duration-300`}>
        {isCollapsed ? (
          <div className="flex justify-center">
            <Link 
              to="/risk-hubs" 
              className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg hover:shadow-xl hover:scale-105 ${isActive('/risk-hubs') ? 'ring-2 ring-blue-300' : ''} transition-all duration-300 overflow-hidden`}
              title="Risk Hubs"
              onClick={handleNavClick}
            >
              <Squares2X2Icon className="h-5 w-5" />
            </Link>
          </div>
        ) : (
          <Link
            to="/risk-hubs"
            className={`flex items-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] ${isActive('/risk-hubs') ? 'ring-2 ring-blue-300' : ''} transition-all duration-300`}
            onClick={handleNavClick}
          >
            <div className="w-6 h-6 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden">
              <Squares2X2Icon className="h-5 w-5" />
            </div>
            <span>Risk Hubs</span>
            <span className="ml-auto bg-white/20 text-xs px-2 py-1 rounded-full font-medium">Specialized</span>
          </Link>
        )}
      </div>

      {/* Prominent Buttons */}
      <div className={`${isCollapsed ? '' : 'px-4'} mt-2 flex flex-col gap-2 transition-all duration-300`}>
        {isCollapsed ? (
          <div className="flex justify-center">
            <Link 
              to="/ai-assistant" 
              className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-semibold shadow-lg hover:shadow-xl hover:scale-105 ${isActive('/ai-assistant') ? 'ring-2 ring-gray-300' : ''} transition-all duration-300 overflow-hidden`}
              title={BRAND.copilot}
              onClick={handleNavClick}
            > 
              <BrainIcon className="h-10 w-10" />
            </Link>
          </div>
        ) : (
          <Link 
            to="/ai-assistant" 
            className={`flex items-center gap-2 px-4 py-3 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] ${isActive('/ai-assistant') ? 'ring-2 ring-gray-300' : ''} transition-all duration-300`}
            onClick={handleNavClick}
          > 
            <div className="rounded-full bg-gray-900 p-2 flex items-center justify-center shadow-xl">
              <BrainIcon className="h-5 w-5" />
            </div>
            <span>{BRAND.copilot}</span>
            <span className="ml-auto bg-white/20 text-xs px-2 py-1 rounded-full font-medium">Assistant</span>
          </Link>
        )}
        
        {/* Alora Intelligence */}
        {isCollapsed ? (
          <div className="flex justify-center mt-2">
            <Link 
              to="/alora-intelligence" 
              className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-xl hover:scale-105 ${isActive('/alora-intelligence') ? 'ring-2 ring-indigo-300' : ''} transition-all duration-300 overflow-hidden`}
              title={`${BRAND.copilotShort} Intelligence`}
              onClick={handleNavClick}
            >
              <EyeIcon className="h-5 w-5" />
            </Link>
          </div>
        ) : (
          <Link
            to="/alora-intelligence"
            className={`flex items-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] ${isActive('/alora-intelligence') ? 'ring-2 ring-indigo-300' : ''} transition-all duration-300`}
            onClick={handleNavClick}
          >
            <div className="w-6 h-6 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden">
              <EyeIcon className="h-5 w-5" />
            </div>
            <span>{BRAND.copilotShort} Intelligence</span>
            <span className="ml-auto bg-white/20 text-xs px-2 py-1 rounded-full font-medium">Layer 3</span>
          </Link>
        )}
      </div>

      {/* Settings & Help */}
      <div className={`mt-auto ${isCollapsed ? '' : 'px-4'} pb-4 flex flex-col gap-2 transition-all duration-300`}>
        {isCollapsed ? (
          <>
            <div className="flex justify-center">
              <Link 
                to="/settings" 
                className={`flex items-center justify-center w-10 h-10 rounded-xl text-base ${isActive('/settings') ? "bg-gray-100 text-gray-900 font-semibold" : "text-gray-700 hover:bg-gray-50"} transition-all duration-300`}
                title="Settings"
                onClick={handleNavClick}
              >
                <Cog6ToothIcon className="h-5 w-5" />
              </Link>
            </div>
            <div className="flex justify-center">
              <Link
                to="/help"
                className={`flex items-center justify-center w-10 h-10 rounded-xl text-base ${isActive('/help') ? "bg-gray-100 text-gray-900 font-semibold" : "text-gray-700 hover:bg-gray-50"} transition-all duration-300`}
                title="Developer Hub"
                onClick={handleNavClick}
              >
                <QuestionMarkCircleIcon className="h-5 w-5" />
              </Link>
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleLogout}
                className="flex items-center justify-center w-10 h-10 rounded-xl text-base text-red-600 hover:bg-red-50 transition-all duration-300"
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </>
        ) : (
          <>
            <Link 
              to="/settings" 
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-base ${isActive('/settings') ? "bg-gray-100 text-gray-900 font-semibold" : "text-gray-700 hover:bg-gray-50"} transition-all duration-300`}
              onClick={handleNavClick}
            >
              <Cog6ToothIcon className="h-5 w-5" />
              <span>Settings</span>
            </Link>
            <Link
              to="/help"
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-base ${isActive('/help') ? "bg-gray-100 text-gray-900 font-semibold" : "text-gray-700 hover:bg-gray-50"} transition-all duration-300`}
              onClick={handleNavClick}
            >
              <QuestionMarkCircleIcon className="h-5 w-5" />
              <span>Developer Hub</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-base text-red-600 hover:bg-red-50 transition-all duration-300"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default Sidebar;